/**
 * NarratorSystem — Manages the appearance of two narrator characters:
 *
 *   1. **Brother Malachar** (Monk) — An ancient monk from the Order of Threia.
 *      Wise, elderly, compassionate. Appears ~every 2-3 levels starting at level 2.
 *      May offer small gifts (metallic/copper key, or a partial heal).
 *
 *   2. **Vorath** — The fallen celestial guardian, bound within the labyrinth.
 *      Resentful, menacing, articulate. Appears ~every 4-5 levels starting at level 4.
 *      Never gives gifts.
 *
 * Messages are sequential per playthrough (building a narrative arc).
 * State persists across save/load so continued games pick up where they left off.
 * Monk and Vorath never appear on the same level.
 *
 * @module NarratorSystem
 */

import {
  NARRATOR_MONK_FIRST_LEVEL,
  NARRATOR_MONK_MIN_INTERVAL,
  NARRATOR_MONK_MAX_INTERVAL,
  NARRATOR_VORATH_FIRST_LEVEL,
  NARRATOR_VORATH_MIN_INTERVAL,
  NARRATOR_VORATH_MAX_INTERVAL,
} from '../config/game-config.js';

// ---------------------------------------------------------------
// Monk Messages  (Brother Malachar)
// ---------------------------------------------------------------
// Each entry: { text, gift? }
// gift types: 'key_metallic', 'key_copper', 'heal_partial', 'heal_full', null
// Gift is delivered to inventory when modal is dismissed.
// Content is unique lore that does NOT duplicate manuscripts or help screen.

/** @type {Array<{text: string, gift: string|null}>} */
const MONK_MESSAGES = Object.freeze([
  {
    text:
      'I am Brother Malachar, last of the\n'
      + 'Order of Threia. I have wandered\n'
      + 'these corridors for longer than I\n'
      + 'can remember. But I remember you.\n'
      + 'I saw you coming in a vision.',
    gift: null,
  },
  {
    text:
      'The labyrinth tests your spirit,\n'
      + 'not your strength. Do not rush.\n'
      + 'A rested soul heals — stand still\n'
      + 'and let the silence mend you.\n'
      + 'Here, take this. It may help.',
    gift: 'key_metallic',
  },
  {
    text:
      'When I was young, my brothers\n'
      + 'taught me that every wall has a\n'
      + 'purpose. Vorath built them to\n'
      + 'trap, but Threia weeps through\n'
      + 'every crack. Listen for her.',
    gift: null,
  },
  {
    text:
      'Five of us gave our souls to\n'
      + 'seal him. I was spared — Threia\n'
      + 'said I had one task left. I think\n'
      + 'you are that task. Take this key\n'
      + 'and press onward.',
    gift: 'key_copper',
  },
  {
    text:
      'I can feel the seal weakening.\n'
      + 'Each soul that escapes strengthens\n'
      + 'it, but each soul that falls...\n'
      + 'feeds him. You must not fall.\n'
      + 'Let me restore some of your vigor.',
    gift: 'heal_partial',
  },
  {
    text:
      'The Agmuth mineral exists between\n'
      + 'worlds. When you carry it, the\n'
      + 'walls lose their claim on you.\n'
      + 'But use it wisely — each fragment\n'
      + 'contains a piece of the binding.',
    gift: null,
  },
  {
    text:
      'Vorath was not always what he is.\n'
      + 'He once carried souls across with\n'
      + 'tenderness. I pity what he became,\n'
      + 'even as I fight to undo his work.\n'
      + 'Compassion is the hardest weapon.',
    gift: 'key_metallic',
  },
  {
    text:
      'My body faded long ago. What you\n'
      + 'see is a memory Threia keeps alive\n'
      + 'so I can guide those like you. I\n'
      + 'do not know how many more times\n'
      + 'she can sustain this fragment of me.',
    gift: 'heal_partial',
  },
  {
    text:
      'The deeper you go, the more he\n'
      + 'presses against the seal. The bats\n'
      + 'you see — they are his thoughts\n'
      + 'given wings. If you hold the totem,\n'
      + 'his thoughts cannot touch you.',
    gift: null,
  },
  {
    text:
      'Brother Aldren was blind, yet he\n'
      + 'navigated the labyrinth better than\n'
      + 'any of us. He said the compass in\n'
      + 'his heart always pointed to freedom.\n'
      + 'I think he meant hope itself.',
    gift: 'key_copper',
  },
  {
    text:
      'I have watched many souls wander\n'
      + 'these halls. Some rage, some weep,\n'
      + 'some go silent. But the ones who\n'
      + 'escape? They never stop moving.\n'
      + 'Rest when needed, but never stop.',
    gift: null,
  },
  {
    text:
      'Threia cannot act directly — the\n'
      + 'binding that seals Vorath also\n'
      + 'limits her. But prayer reaches her.\n'
      + 'Each press of devotion is a thread\n'
      + 'she can pull to find you.',
    gift: 'heal_full',
  },
  {
    text:
      'I feel my time growing short.\n'
      + 'The memory Threia holds of me is\n'
      + 'thinning, like ink in water. But\n'
      + 'if you escape, perhaps a part of\n'
      + 'all of us escapes with you.',
    gift: 'key_copper',
  },
  {
    text:
      'This may be the last time we speak.\n'
      + 'Remember: the labyrinth changes,\n'
      + 'but the exit is always there. Vorath\n'
      + 'hides it, Threia reveals it. Keep\n'
      + 'faith. I believe in you, wanderer.',
    gift: 'heal_full',
  },
]);

// ---------------------------------------------------------------
// Vorath Messages
// ---------------------------------------------------------------
// Vorath never gives gifts. His tone: resentful, articulate, menacing,
// philosophical in a dark way, believes his actions are justified.

/** @type {Array<{text: string}>} */
const VORATH_MESSAGES = Object.freeze([
  {
    text:
      'You think you are the first to\n'
      + 'reach this deep? I have watched\n'
      + 'a thousand souls crumble where\n'
      + 'you now stand. You will be no\n'
      + 'different.',
  },
  {
    text:
      'They call me the villain. But I\n'
      + 'was the one who carried them\n'
      + 'gently, once. It was their traces\n'
      + 'that changed me — their fear,\n'
      + 'their sorrow. I merely adapted.',
  },
  {
    text:
      'Threia abandoned this place long\n'
      + 'before the monks came. She speaks\n'
      + 'of mercy but hides behind a seal.\n'
      + 'I am the only honest thing in\n'
      + 'this labyrinth.',
  },
  {
    text:
      'The old monk still wanders, does\n'
      + 'he? A ghost clinging to a dead\n'
      + 'goddess. He tells you to have\n'
      + 'hope. I will tell you the truth:\n'
      + 'hope is the cruelest maze of all.',
  },
  {
    text:
      'Every wall you see, I dreamed.\n'
      + 'Every dead end, I designed. The\n'
      + 'water, the fire, the poison —\n'
      + 'each one is a question: how much\n'
      + 'can you endure before you break?',
  },
  {
    text:
      'The monks thought four relics\n'
      + 'could hold me. They were wrong.\n'
      + 'The seal weakens with every soul\n'
      + 'I consume. And you — you are so\n'
      + 'full of delicious determination.',
  },
  {
    text:
      'Do you hear the bats? Those are\n'
      + 'my eyes, my hands. Even sealed,\n'
      + 'I reach through. Collect your\n'
      + 'trinkets — each one draws my\n'
      + 'attention closer.',
  },
  {
    text:
      'Threia weeps for you. Can you\n'
      + 'feel it? That faint warmth when\n'
      + 'you pray? It is not salvation.\n'
      + 'It is pity. And pity has never\n'
      + 'unlocked a single door.',
  },
  {
    text:
      'I was not born cruel. I was made\n'
      + 'guardian of a threshold between\n'
      + 'worlds. But the souls that passed\n'
      + 'through me left wounds. You would\n'
      + 'have done the same in my place.',
  },
  {
    text:
      'The deeper you go, the closer you\n'
      + 'come to where I wait. The seal\n'
      + 'cracks a little more each day.\n'
      + 'One day soon, there will be no\n'
      + 'labyrinth — only me.',
  },
]);

// ---------------------------------------------------------------
// NarratorSystem class
// ---------------------------------------------------------------

export default class NarratorSystem {
  /**
   * @param {object} opts
   * @param {number} opts.monkIndex      - Current sequential index into MONK_MESSAGES
   * @param {number} opts.vorathIndex    - Current sequential index into VORATH_MESSAGES
   * @param {number} opts.monkNextLevel  - Next level the monk is scheduled to appear
   * @param {number} opts.vorathNextLevel - Next level Vorath is scheduled to appear
   */
  constructor({ monkIndex = 0, vorathIndex = 0, monkNextLevel = 0, vorathNextLevel = 0 } = {}) {
    /** @type {number} */
    this.monkIndex = monkIndex;
    /** @type {number} */
    this.vorathIndex = vorathIndex;
    /** @type {number} Next level monk should appear (0 = unscheduled, will be computed). */
    this.monkNextLevel = monkNextLevel;
    /** @type {number} Next level Vorath should appear (0 = unscheduled, will be computed). */
    this.vorathNextLevel = vorathNextLevel;

    // Initialize schedules if not yet set (new game)
    if (this.monkNextLevel <= 0) {
      this.monkNextLevel = this._randomBetween(
        NARRATOR_MONK_FIRST_LEVEL,
        NARRATOR_MONK_FIRST_LEVEL + 1,
      );
    }
    if (this.vorathNextLevel <= 0) {
      this.vorathNextLevel = this._randomBetween(
        NARRATOR_VORATH_FIRST_LEVEL,
        NARRATOR_VORATH_FIRST_LEVEL + 1,
      );
    }
  }

  /**
   * Check what narrator event (if any) should trigger on a given level.
   * Returns null if no narrator should appear.
   * If both would collide, the monk takes priority and Vorath is deferred +1 level.
   *
   * @param {number} level - The current game level
   * @returns {{ speaker: 'monk'|'vorath', name: string, text: string, gift: string|null }|null}
   */
  check(level) {
    const monkReady = level >= this.monkNextLevel && this.monkIndex < MONK_MESSAGES.length;
    const vorathReady = level >= this.vorathNextLevel && this.vorathIndex < VORATH_MESSAGES.length;

    // Neither ready
    if (!monkReady && !vorathReady) return null;

    // Both ready on same level — monk takes priority, defer Vorath
    if (monkReady && vorathReady) {
      return this._triggerMonk();
    }

    if (monkReady) return this._triggerMonk();
    if (vorathReady) return this._triggerVorath();

    return null;
  }

  /**
   * After a narrator event is shown and dismissed, call this to advance
   * the index and schedule the next appearance.
   *
   * @param {'monk'|'vorath'} speaker
   */
  advance(speaker) {
    if (speaker === 'monk') {
      this.monkIndex++;
      this.monkNextLevel = this.monkNextLevel + this._randomBetween(
        NARRATOR_MONK_MIN_INTERVAL,
        NARRATOR_MONK_MAX_INTERVAL,
      );
      // If Vorath was deferred by collision, push him forward too
      if (this.vorathNextLevel <= this.monkNextLevel) {
        // Ensure Vorath doesn't collide on the same level as monk's new schedule
        // This is fine — they'll be checked again on that level
      }
    } else {
      this.vorathIndex++;
      this.vorathNextLevel = this.vorathNextLevel + this._randomBetween(
        NARRATOR_VORATH_MIN_INTERVAL,
        NARRATOR_VORATH_MAX_INTERVAL,
      );
    }
  }

  /**
   * Serialize narrator state for save.
   * @returns {{ narratorMonkIndex: number, narratorVorathIndex: number, narratorMonkNextLevel: number, narratorVorathNextLevel: number }}
   */
  serialize() {
    return {
      narratorMonkIndex: this.monkIndex,
      narratorVorathIndex: this.vorathIndex,
      narratorMonkNextLevel: this.monkNextLevel,
      narratorVorathNextLevel: this.vorathNextLevel,
    };
  }

  /**
   * Reset narrator state for a new game.
   */
  reset() {
    this.monkIndex = 0;
    this.vorathIndex = 0;
    this.monkNextLevel = this._randomBetween(
      NARRATOR_MONK_FIRST_LEVEL,
      NARRATOR_MONK_FIRST_LEVEL + 1,
    );
    this.vorathNextLevel = this._randomBetween(
      NARRATOR_VORATH_FIRST_LEVEL,
      NARRATOR_VORATH_FIRST_LEVEL + 1,
    );
  }

  // --- private helpers ---

  /** @private */
  _triggerMonk() {
    const msg = MONK_MESSAGES[this.monkIndex];
    return {
      speaker: 'monk',
      name: 'Brother Malachar',
      text: msg.text,
      gift: msg.gift || null,
    };
  }

  /** @private */
  _triggerVorath() {
    const msg = VORATH_MESSAGES[this.vorathIndex];
    return {
      speaker: 'vorath',
      name: 'Vorath',
      text: msg.text,
      gift: null,
    };
  }

  /**
   * @private
   * @param {number} min
   * @param {number} max
   * @returns {number} Integer in [min, max] inclusive
   */
  _randomBetween(min, max) {
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  /** Total monk messages available. */
  static get MONK_COUNT() { return MONK_MESSAGES.length; }

  /** Total Vorath messages available. */
  static get VORATH_COUNT() { return VORATH_MESSAGES.length; }
}
